import type { EntityState } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  createSelectorDeepEqual,
  DEFAULT_SCOPE_NAME,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  ReportDashboardTemplate,
  ReportDashboardTemplateApiEntity,
  ReportDashboardTemplateComponent,
  ReportDashboardTemplatesResponse,
} from '@core/reports-types';

import { safeReportsStateSelector } from '../helpers/safe-selector';
import {
  prefixTemplateLibraryComponentId,
  unprefixTemplateLibraryComponentId,
} from '../helpers/template-library-component-id';
import { ReportsLibraryComponentsSelectors } from '../library-components/library-components.store';

const selectDashboardTemplateId = ({ id }: { id: string }): string => id;
const stripDashboardComponentsRefs = <T extends { components: ReportDashboardTemplateComponent[] }>(
  dashboard: T,
): Omit<T, 'components'> & { componentIds: string[] } => ({
  ...dashboard,
  components: [],
  componentIds: dashboard.components.map(c => c.libraryId),
});

const addGridPositions = (
  dashboard: ReportDashboardTemplateApiEntity,
): ReportDashboardTemplateApiEntity &
  Pick<ReportDashboardTemplate, 'gridPositions' | 'verticalGridPositions'> => ({
  ...dashboard,
  gridPositions: dashboard.components.reduce(
    (acc, curr) => ({
      ...acc,
      [prefixTemplateLibraryComponentId(curr.libraryId, dashboard.id)]: curr.grid,
    }),
    {},
  ),
  verticalGridPositions: dashboard.components.reduce(
    (acc, curr) => ({
      ...acc,
      [prefixTemplateLibraryComponentId(curr.libraryId, dashboard.id)]: curr.verticalGrid,
    }),
    {},
  ),
});

// Prefix to avoid id collision with regular library components
const prefixComponentIds = (dashboard: ReportDashboardTemplate): ReportDashboardTemplate => ({
  ...dashboard,
  componentIds: dashboard.componentIds.map(componentId =>
    prefixTemplateLibraryComponentId(componentId, dashboard.id),
  ),
});

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportDashboardTemplate>()('ReportsDashboardTemplates', {
    baseSelector: (root): EntityState<ReportDashboardTemplate> =>
      safeReportsStateSelector(root, namespace, initialState),
    customActions: adapter => ({
      resetAll: state => adapter.resetAll(state),

      fetchCollectionSuccess: (state, payload: { data: ReportDashboardTemplatesResponse }) =>
        pipeState(
          state,
          state =>
            adapter.setMany(
              state,
              payload.data.dashboardTemplates.map(dashboard =>
                prefixComponentIds(stripDashboardComponentsRefs(addGridPositions(dashboard))),
              ),
            ),

          next =>
            adapter.markAsLoaded(next, {
              entities: payload.data.dashboardTemplates.map(selectDashboardTemplateId),
              scope: DEFAULT_SCOPE_NAME,
            }),
        ),
      fetchCollectionFailed: (state, error: any) => adapter.setError(state, error),
    }),
  });

const meta = createSelectorDeepEqual(selectors.meta(), metaState => ({
  pending: metaState.isPending,
  loaded: metaState.isLoaded,
  failed: metaState.isFailed,
  error: metaState.error,
}));

const dashboardTemplateWithReferences = (id: string) =>
  createSelectorDeepEqual(
    selectors.entityMap,
    ReportsLibraryComponentsSelectors.entityMap,
    (
      dashboards,
      components,
    ): ReportDashboardTemplate & { components: ReportDashboardTemplateComponent[] } => {
      const dashboard = dashboards[id]!;
      return {
        ...dashboard,
        components: dashboard.componentIds.map(componentId => {
          const component = components[componentId]!;
          return {
            ...component,
            grid: dashboard.gridPositions[componentId]!,
            verticalGrid: dashboard.verticalGridPositions[componentId]!,
            templateId: unprefixTemplateLibraryComponentId(component.libraryId),
            origin: 'template',
          };
        }),
      };
    },
  );

export const ReportsDashboardTemplatesSelectors = {
  entities: selectors.entities(),
  meta,
  dashboardTemplateWithReferences,
};

export const ReportsDashboardTemplatesActions = {
  fetchCollection: actions.fetchCollection,
  fetchCollectionSuccess: actions.fetchCollectionSuccess,
  fetchCollectionFailed: actions.fetchCollectionFailed,
};

export {
  namespace as ReportsDashboardTemplatesNamespace,
  reducer as ReportsDashboardTemplatesReducer,
};
